/// <amd-module name="Search/Wiki/Flux/ActionCreator" />
import * as React from "react";
import * as Constants from "Search/Wiki/Constants";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { ActionsHub } from "Search/Wiki/Flux/ActionsHub";
import { deserialize } from "Search/Shared/Utils";
import { EntityTypeUrlParam } from "Search/Wiki/Constants"
import { Filter, FILTER_CHANGE_EVENT, IFilterState, FILTER_RESET_EVENT } from "SearchUI/Utilities/Filter";
import { IAggregatedState } from "Search/Wiki/Flux/StoresHub";
import { IVssPageContext } from "VSS/Platform/Context";
import { onClickFPS } from "VSS/Platform/FPS";
import { OrgInfoDataProviderSource } from "Search/Shared/Base/Sources/OrgInfoDataProviderSource";
import { PageSource } from "Search/Shared/Base/Sources/PageSource";
import { SearchSourceType } from "Search/Shared/Base/ActionsHub2";
import { TelemetryWriter } from "Search/Wiki/Flux/Sources/TelemetryWriter";
import { WikiSearchSource } from "Search/Wiki/Flux/Sources/WikiSearchSource";
import { WikiSearchRequest, WikiSearchResponse } from "Search/WebApi/Contracts/Search.Shared";
import { SearchStatus } from "Search/Shared/Base/Stores/SearchStore";

export interface Sources {
    searchSource: WikiSearchSource;
    pageSource: PageSource;
    orgInfoDataProviderSource: OrgInfoDataProviderSource;
}

export class ActionCreator {
    constructor(
        private readonly pageContext: IVssPageContext,
        private readonly actionsHub: ActionsHub,
        private readonly filter: Filter,
        private readonly sources: Sources,
        private readonly getAggregatedState: () => IAggregatedState,
        private readonly telemetryWriter: TelemetryWriter
    ) {
        this.filter.subscribe(this.applyFilter, FILTER_CHANGE_EVENT);
        this.filter.subscribe(this.onFilterResetClick, FILTER_RESET_EVENT);
    }

    public dispose = (): void => {
        this.filter.unsubscribe(this.applyFilter, FILTER_CHANGE_EVENT);
        this.filter.unsubscribe(this.onFilterResetClick, FILTER_RESET_EVENT);
    }

    public loadInitialState = (text: string, filtersString?: string, takeResults?: string): void => {
        const request = {
            searchText: text,
            $skip: 0,
            $top: takeResults && !isNaN(parseInt(takeResults)) ? parseInt(takeResults) : Constants.WikiSearchTakeResults,
            $orderBy: [],
            includeFacets: true,
            filters: filtersString ? deserialize<{ [key: string]: string[] }>(filtersString) : {}
        } as WikiSearchRequest;

        this.actionsHub.pageInitializationStarted.invoke({ request });

        this.sources
            .searchSource
            .getWikiSearchResults(request, ({ response, source }) => {
                this._onSearchSucceded(request, response, source, false);
            }, (error => {
                this._onSearchFailed(error);
            }));
    }

    public openSearchInNewTab = (searchText: string): void => {
        const { searchStoreState } = this.getAggregatedState();
        this.sources.pageSource.navigateToNewSearch(EntityTypeUrlParam, searchText, searchStoreState.query.filters);
        this.telemetryWriter.publish(Constants.CustomerIntelligenceConstants.SearchInNewTab);
    }

    public applySearchText = (searchText: string) => {
        const aggregatedState = this.getAggregatedState();
        const { searchStoreState } = aggregatedState;

        this.performSearch(searchText, searchStoreState.query.filters, false);
    }

    public performSearch = (
        searchText: string,
        searchFilters: { [key: string]: string[]; },
        loadMoreResults?: boolean,
    ): void => {
        searchText = searchText && searchText.trim();
        if (!searchText) {
            return;
        }

        const query = {
            $orderBy: [],
            searchText: searchText,
            $skip: 0,
            $top: this.getInitialTake(),
            filters: searchFilters,
            includeFacets: true
        } as WikiSearchRequest;

        this._doSearch(query, loadMoreResults);
    }

    public toggleFilterPane = (): void => {
        this.actionsHub.filterPaneToggled.invoke(undefined);
    }

    public fetchMoreItems = (newSkip: number, newTop: number): void => {
        const searchStoreState = this.getAggregatedState().searchStoreState;

        if (searchStoreState.searchStatus !== SearchStatus.Loading) {
            const query = {
                ...searchStoreState.query,
                $skip: newSkip,
                $top: newTop,
                includeFacets: true
            };

            this._doSearch(query, true);
        }
    }

    /**
    *  Send query on landing page scenario to update searchstore to an adequate state
    */
    public showLandingPage = (filter?: string): void => {
        const request = {
            $orderBy: [],
            $skip: 0,
            $top: this.getInitialTake(),
            searchText: "",
            includeFacets: true,
            filters: filter ? deserialize<{ [key: string]: string[] }>(filter) : {}
        } as WikiSearchRequest;

        this.actionsHub.pageInitializationStarted.invoke({ request, isLandingPage: true });
    }

    public applyFilter = (filterState: IFilterState): void => {
        const currentFilterState = this.filter.getState();
        const aggregatedState = this.getAggregatedState();
        const { filterStoreState, searchStoreState } = aggregatedState;
        const { filterItems } = filterStoreState;

        const filters: { [key: string]: string[] } = {};
        Object
            .keys(currentFilterState)
            .forEach((key) => {
                const value = currentFilterState[key].value;
                if (!value || value.length === 0) {
                    return;
                }

                if (filterItems[key].enabled) {
                    filters[key] = value;
                }
            });

        // Create a new search query with updated filters.
        const query = {
            ...searchStoreState.query,
            $skip: 0,
            $top: this.getInitialTake(),
            filters: filters,
            includeFacets: true
        } as WikiSearchRequest;

        this._doSearch(query);
    }

    /**
    * Method exposed for L0 testing purposes.
    */
    public applyFilters = (filters: { [key: string]: string[] }): void => {
        const filterState: IFilterState = {};

        Object.keys(filters)
            .forEach(key => {
                filterState[key] = { value: filters[key] };
            });

        this.filter.setState(filterState);
    }

    public onRemoveSearchText = () => {
        this.telemetryWriter.publish(Constants.CustomerIntelligenceConstants.SearchTextRemoved);
    }

    public notifyResultsRendered = (): void => {
        const { searchStoreState } = this.getAggregatedState();
        const data = {
            responseActivityId: "", // New REST APi does not have activity id
            searchSource: searchStoreState.source ? SearchSourceType[searchStoreState.source] : ""
        };

        this.telemetryWriter.notifyResultsRendered(data);
    }

    public notifyFeedbackMailLinkClicked = (): void => {
        this.telemetryWriter.publish(Constants.CustomerIntelligenceConstants.FeedbackMailLinkClicked);
    }

    public publishZeroData = (scenarioType: string): void => {
        this.telemetryWriter.publish(Constants.CustomerIntelligenceConstants.ZeroData, { scenarioType });
    }

    public clickAccountLink = (): void => {
        this.telemetryWriter.publish(Constants.CustomerIntelligenceConstants.AccountLinkClicked);
    }

    public clickAccountButton = (): void => {
        const { searchStoreState } = this.getAggregatedState();
        this.sources.pageSource.openCollectionSearch(EntityTypeUrlParam, searchStoreState.query.searchText);
        this.telemetryWriter.publish(Constants.CustomerIntelligenceConstants.AccountButtonClicked);
    }

    public onFilterResetClick = (): void => {
        this.actionsHub.filterReset.invoke({});
        this.telemetryWriter.publish(Constants.CustomerIntelligenceConstants.FilterResetClicked);
    }

    public publishNotificationBanner = (scenarioType: string): void => {
        this.telemetryWriter.publish(Constants.CustomerIntelligenceConstants.NotificationBanner, { scenarioType });
    }

    public errorNotificationBannerDismissed = (): void => {
        this.actionsHub.errorNotificationBannerDismissed.invoke({});
    }

    public clickFileNameLink = (link: string, evt: React.MouseEvent<HTMLAnchorElement> | React.KeyboardEvent<HTMLAnchorElement>): void => {
        // Instead of redirecting the browser, use Fast past switch to wiki page.
        onClickFPS(this.pageContext, link, true, evt);
    }

    private _doSearch(request: WikiSearchRequest, fetchMoreScenario?: boolean): void {
        this.actionsHub.searchStarted.invoke({ request, fetchMoreScenario });
        this.sources
            .searchSource
            .getWikiSearchResults(request, ({ response, source }) => {
                this._onSearchSucceded(request, response, source, fetchMoreScenario);
            }, (error => {
                this._onSearchFailed(error);
            })
            );
    }

    private _onSearchSucceded = (request: WikiSearchRequest, response: WikiSearchResponse, source: SearchSourceType, fetchMoreScenario?: boolean): void => {
        this.actionsHub.resultsLoaded.invoke({ response, request, source, fetchMoreScenario });
    }

    private _onSearchFailed = (error: any): void => {
        this.actionsHub.searchFailed.invoke(error);
    }

    private getInitialTake = (): number => {
        const infiniteScroll = isFeatureFlagEnabled(this.pageContext, Constants.FeatureFlagConstants.InfiniteScroll, false);
        return infiniteScroll ? Constants.WikiSearchInfiniteScrollTakeResults : Constants.WikiSearchTakeResults;
    }
}
